import React from 'react';
import { compose, lifecycle, withHandlers, withStateHandlers } from 'recompose';
import classnames from 'classnames';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import './TestBotButton.scss';
import { connect } from 'react-redux';
import {
  SafeTimeout,
  withSafeTimeout,
} from '../../../../../../common/hocs/withSafeTimeout';
import { StoreState } from '@mm/state/store-interfaces';
import { getFbPageIdSelector } from '@mm/state/selectors/viewSelectors';
import SendToMessenger from '../../../../../../common/components/facebook-plugin-components/SendToMessenger/SendToMessenger';
import MessengerIcon from '../../../../../../dashboard/pages/RegisterFlow/ConnectFacebook/MessengerIcon';
import { TooltipGuard } from '../../../../../../modules/permissions/RequiresPermissions';
import { LifeCycleThis } from '@mm/utils';
import facebookSDKActions from '@mm/state/actions/facebookActions';

enum ACTIVE_BUTTONS {
  SEND_TO_MESSENGER = 'SEND_TO_MESSENGER',
  OPEN_MESSENGER = 'OPEN_MESSENGER',
}

type OuterProps = {
  searchToken: string;
};

type InnerProps = ReturnType<typeof mapStateToProps> & {
  activeButton: ACTIVE_BUTTONS;
  setActiveButton: (active: ACTIVE_BUTTONS) => void;
  openMessenger: (e) => void;
  sendToMessengerRendered: boolean;
  fbSDKReady: boolean | null;
  setFbSDKReady: (ready: boolean) => void;
  setSendToMessengerRendered: (hasRendered: boolean) => void;
  safeTimeout: SafeTimeout;
  onMessengerClick: () => void;
};

type Props = OuterProps & InnerProps;

const openMessenger =
  ({ fbPageId, setActiveButton }: Props) =>
  () => {
    window.open(`http://m.me/${fbPageId}`);
    setActiveButton(ACTIVE_BUTTONS.SEND_TO_MESSENGER);
  };

const onMessengerClick =
  ({ setActiveButton, safeTimeout }: Props) =>
  () => {
    safeTimeout(() => {
      setActiveButton(ACTIVE_BUTTONS.OPEN_MESSENGER);
    }, 500);

    safeTimeout(() => {
      setActiveButton(ACTIVE_BUTTONS.SEND_TO_MESSENGER);
    }, 8000);
  };

const mapStateToProps = (state: StoreState) => ({
  fbPageId: getFbPageIdSelector(state),
  fbAppId: state.env.fbAppId,
});

const stateUpdaters = {
  setFbSDKReady: () => (fbSDKReady: boolean) => ({ fbSDKReady }),
  setActiveButton: () => activeButton => ({ activeButton }),
  setSendToMessengerRendered: () => sendToMessengerRendered => ({
    sendToMessengerRendered,
  }),
};

type InitialState = {
  fbSDKReady: boolean | null;
  activeButton: ACTIVE_BUTTONS;
  sendToMessengerRendered: boolean;
};

const initialState: InitialState = {
  fbSDKReady: null,
  activeButton: ACTIVE_BUTTONS.SEND_TO_MESSENGER,
  sendToMessengerRendered: false,
};

async function componentDidMount(this: LifeCycleThis<Props>) {
  const { setFbSDKReady } = this.props;

  const SDK = await facebookSDKActions.getFacebookSDK(4);
  setFbSDKReady(!!SDK);
}

const enhance = compose<InnerProps, OuterProps>(
  withSafeTimeout,
  connect(mapStateToProps),
  withStateHandlers(initialState, stateUpdaters),
  withHandlers({ openMessenger, onMessengerClick }),
  lifecycle<Props, {}>({ componentDidMount }),
);

const TestBotButton = enhance((props: Props) => {
  const {
    fbAppId,
    fbPageId,
    fbSDKReady,
    searchToken,
    activeButton,
    openMessenger,
    onMessengerClick,
    setSendToMessengerRendered,
    sendToMessengerRendered,
  } = props;

  const sendToMessengerActive =
    activeButton === ACTIVE_BUTTONS.SEND_TO_MESSENGER && fbSDKReady !== false;
  const hasNeededProps = fbAppId && fbPageId && searchToken;

  if (!hasNeededProps) {
    return null;
  }

  const sendToMessengerClassList = classnames({
    'test-bot-button--messenger-not-loaded': !sendToMessengerRendered,
    'test-bot-button--hidden': !sendToMessengerActive,
  });

  return (
    <div className="test-bot-button">
      <div className="test-bot-button__button-wrapper">
        <div className="test-bot-button__messenger">
          {sendToMessengerActive ? (
            <div className={sendToMessengerClassList}>
              <SendToMessenger
                appId={fbAppId}
                pageId={fbPageId}
                passthroughParams={searchToken}
                onOptIn={onMessengerClick}
                onRendered={() => setSendToMessengerRendered(true)}
                triggerRender={sendToMessengerActive}
                size="large"
              />

              <MessengerIcon
                className="test-bot-button__messenger-icon"
                hoverColor="#fff"
                size={20}
              />
            </div>
          ) : (
            <div
              className="test-bot-button__open-messenger-button"
              onClick={openMessenger}
            >
              Open Messenger
            </div>
          )}
        </div>
      </div>
      <TooltipGuard>
        {isAllowed =>
          isAllowed ? (
            <CopyToClipboard
              text={`http://m.me/${fbPageId}?ref=${searchToken}`}
            >
              <span className="test-bot-button__copy-link">Copy Link</span>
            </CopyToClipboard>
          ) : (
            <></>
          )
        }
      </TooltipGuard>
    </div>
  );
});

export default TestBotButton;
